import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const TEXT_FIELDS = [
  { k: "announcement", label: "Announcement strip" },
  { k: "phone", label: "Phone number" },
  { k: "site_url", label: "Site URL" },
  { k: "hero_headline", label: "Hero headline" },
  { k: "hero_subtitle", label: "Hero subtitle" },
  { k: "hero_cta", label: "Hero button text" },
  { k: "rachel_bio", label: "Rachel's intro" },
  { k: "custom_title", label: "Custom section title" },
  { k: "custom_subtitle", label: "Custom section subtitle" },
  { k: "designed_by_name", label: "Designed by — name" },
  { k: "designed_by_email", label: "Designed by — email" },
  { k: "social_instagram", label: "Instagram URL (empty = hidden)" },
  { k: "social_tiktok", label: "TikTok URL (empty = hidden)" },
  { k: "social_email", label: "Email address (shown in footer, used for all email)" },
];

const NUMBER_FIELDS = [
  { k: "shipping_flat_rate_cents", label: "Flat shipping rate (cents)" },
  { k: "free_shipping_threshold_cents", label: "Free shipping threshold (cents, 0 = off)" },
];

export default function Content() {
  const [settings, setSettings] = useState(null);
  const [pages, setPages] = useState(null);
  const [page, setPage] = useState(null);
  const [tab, setTab] = useState("store");

  useEffect(() => {
    api("/admin/settings").then(setSettings).catch(() => setSettings({}));
    api("/admin/pages").then(setPages).catch(() => setPages([]));
  }, []);

  const saveSettings = async () => {
    try {
      await api("/admin/settings", { method: "PUT", body: settings });
      toast("Settings saved — refresh the storefront to see it live");
    } catch (e) { toast.error(e.message); }
  };

  const savePage = async () => {
    try {
      await api(`/admin/pages/${page.slug}`, { method: "PUT", body: page });
      toast("Page saved");
      api("/admin/pages").then(setPages);
    } catch (e) { toast.error(e.message); }
  };

  if (!settings) return <p className="text-muted font-bold">Loading the recipe box…</p>;

  return (
    <div data-testid="admin-content">
      <div className="flex gap-2">
        <button data-testid="admin-content-tab-store" onClick={() => setTab("store")} className={`px-4 py-2 rounded-full text-sm font-bold ${tab === "store" ? "bg-candy-glow text-candy-deep" : "bg-white text-ink/60"}`}>Store copy</button>
        <button data-testid="admin-content-tab-pages" onClick={() => setTab("pages")} className={`px-4 py-2 rounded-full text-sm font-bold ${tab === "pages" ? "bg-candy-glow text-candy-deep" : "bg-white text-ink/60"}`}>Pages & policies</button>
      </div>

      {tab === "store" && (
        <div className="mt-6 bg-white rounded-3xl border border-candy-soft/70 p-6">
          <h2 className="font-display font-semibold text-xl mb-4">Editable copy — every word on the storefront lives here</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {TEXT_FIELDS.map((f) => (
              <label key={f.k}>
                <span className="text-xs font-bold text-ink/70">{f.label}</span>
                <input data-testid={`admin-setting-${f.k}`} value={settings[f.k] || ""} onChange={(e) => setSettings({ ...settings, [f.k]: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-xl bg-candy-mist border border-candy-soft text-sm font-semibold" />
              </label>
            ))}
            {NUMBER_FIELDS.map((f) => (
              <label key={f.k}>
                <span className="text-xs font-bold text-ink/70">{f.label}</span>
                <input data-testid={`admin-setting-${f.k}`} type="number" value={settings[f.k] || 0} onChange={(e) => setSettings({ ...settings, [f.k]: Number(e.target.value) })} className="mt-1 w-full px-3 py-2 rounded-xl bg-candy-mist border border-candy-soft text-sm font-semibold" />
              </label>
            ))}
          </div>
          <p className="text-xs text-muted font-semibold mt-3">Shipping values are placeholders — set your real rates before launch. Note: {settings.free_shipping_note || ""}</p>
          <button data-testid="admin-settings-save" onClick={saveSettings} className="mt-5 px-8 py-3 rounded-full bg-candy text-white font-bold hover:bg-candy-deep transition-colors duration-200">Save settings</button>
        </div>
      )}

      {tab === "pages" && (
        <div className="mt-6 grid lg:grid-cols-3 gap-4 items-start">
          <div className="bg-white rounded-3xl border border-candy-soft/70 p-4">
            <h2 className="font-display font-semibold text-lg mb-2">Pages</h2>
            {!pages ? <p className="text-muted text-sm">Loading…</p> : pages.map((p) => (
              <button key={p.slug} data-testid={`admin-page-select-${p.slug}`} onClick={() => setPage({ ...p })} className={`w-full text-left px-3 py-2.5 rounded-xl font-bold text-sm mb-1 transition-colors duration-200 ${page?.slug === p.slug ? "bg-candy-glow text-candy-deep" : "hover:bg-candy-mist"}`}>
                {p.title} {p.published ? <span className="text-emerald-600">· live</span> : <span className="text-amber-600">· draft</span>}
              </button>
            ))}
          </div>
          {page && (
            <div className="lg:col-span-2 bg-white rounded-3xl border border-candy-soft/70 p-6" data-testid={`admin-page-editor-${page.slug}`}>
              <label className="block">
                <span className="text-xs font-bold text-ink/70">Title</span>
                <input data-testid="admin-page-title" value={page.title || ""} onChange={(e) => setPage({ ...page, title: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-xl bg-candy-mist border border-candy-soft text-sm font-semibold" />
              </label>
              <label className="block mt-3">
                <span className="text-xs font-bold text-ink/70">Intro</span>
                <input data-testid="admin-page-intro" value={page.intro || ""} onChange={(e) => setPage({ ...page, intro: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-xl bg-candy-mist border border-candy-soft text-sm font-semibold" />
              </label>
              {(page.sections || []).map((s, i) => (
                <div key={i} className="mt-3 bg-candy-mist rounded-2xl p-3">
                  <input data-testid={`admin-page-section-heading-${i}`} value={s.heading} onChange={(e) => { const sections = [...page.sections]; sections[i] = { ...s, heading: e.target.value }; setPage({ ...page, sections }); }} className="w-full px-3 py-2 rounded-xl bg-white border border-candy-soft text-sm font-bold" placeholder="Heading / question" />
                  <textarea data-testid={`admin-page-section-body-${i}`} rows={3} value={s.body} onChange={(e) => { const sections = [...page.sections]; sections[i] = { ...s, body: e.target.value }; setPage({ ...page, sections }); }} className="mt-2 w-full px-3 py-2 rounded-xl bg-white border border-candy-soft text-sm font-semibold resize-none" placeholder="Body / answer" />
                </div>
              ))}
              <button data-testid="admin-page-add-section" onClick={() => setPage({ ...page, sections: [...(page.sections || []), { heading: "New section", body: "" }] })} className="mt-3 px-4 py-2 rounded-full bg-candy-mist text-candy-deep text-xs font-bold">+ Add section</button>
              <label className="flex items-center gap-3 mt-4 bg-butter-soft rounded-xl px-3 py-2.5">
                <input data-testid="admin-page-publish-toggle" type="checkbox" checked={!!page.published} onChange={(e) => setPage({ ...page, published: e.target.checked })} className="w-4 h-4 accent-pink-500" />
                <span className="text-sm font-bold">Published — I've reviewed this copy and it can go live</span>
              </label>
              <button data-testid="admin-page-save" onClick={savePage} className="mt-4 px-8 py-3 rounded-full bg-candy text-white font-bold hover:bg-candy-deep transition-colors duration-200">Save page</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
